import type React from 'react'
import { useEffect, useRef, useState } from 'react'

import { normalizeHexColor } from '@/lib/shapes/text-on-path-colors'

import type { TextOnPathConfigPatch } from '../types/control-props'

/**
 * The Color control's state: the hex text field's raw string (re-synced from `colorHex` unless
 * the field has focus), plus `apply` for a swatch or native-picker pick.
 */
export function useHexColorInput(
  colorHex: string,
  onConfigChange: (patch: TextOnPathConfigPatch) => void
) {
  const normalized = normalizeHexColor(colorHex)
  const [hexInput, setHexInput] = useState(() => normalized ?? colorHex)
  const isFocusedRef = useRef(false)

  useEffect(() => {
    if (isFocusedRef.current) return
    setHexInput(normalizeHexColor(colorHex) ?? colorHex)
  }, [colorHex])

  const apply = (next: string) => {
    setHexInput(next)
    onConfigChange({ colorHex: next })
  }

  const handleHexFocus = () => {
    isFocusedRef.current = true
  }

  const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value

    setHexInput(raw)
    const parsed = normalizeHexColor(raw)

    // Only commit a complete colour: patching on every keystroke would repaint the
    // canvas through every partial prefix of what the user is typing.
    if (parsed != null) onConfigChange({ colorHex: parsed })
  }

  const handleHexBlur = () => {
    isFocusedRef.current = false
    setHexInput(normalizeHexColor(colorHex) ?? colorHex)
  }

  return { normalized, hexInput, apply, handleHexFocus, handleHexChange, handleHexBlur }
}
